import React from 'react'
import { Card } from '@/shared/ui/primitives'
import { useForceTheme } from '@/shared/ui/hooks'
import { COMMAND_CENTER_CAPABILITIES } from './command-center-capabilities'

export interface AuthLayoutProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  footer?: React.ReactNode
  headerSlot?: React.ReactNode
}

export function AuthLayout({ title, subtitle, children, footer, headerSlot }: AuthLayoutProps) {
  useForceTheme('dark')

  return (
    <div
      data-slot="auth-layout"
      className="min-h-screen bg-sidebar font-marketing lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]"
    >
      <aside className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-end lg:gap-14 lg:p-12 xl:p-16">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/auth-background.jpg')" }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-sidebar via-sidebar/70 to-sidebar/25"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-sidebar/70 via-transparent to-sidebar"
          aria-hidden
        />
        <div className="relative z-10 max-w-xl">
          <p className="text-2xs font-semibold uppercase tracking-widest text-blaze">
            Command Center
          </p>
          <h2 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-foreground xl:text-5xl">
            Real data.
            <br />
            Real insights.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-fg-2">
            Track every hunt, analyze blind performance, and understand conditions — all from one
            dashboard.
          </p>
          <ul className="mt-10 flex flex-col gap-5">
            {COMMAND_CENTER_CAPABILITIES.map((capability) => (
              <li key={capability.key} className="flex items-start gap-4">
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-blaze-soft ring-1 ring-blaze/25">
                  {capability.icon}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-foreground">{capability.label}</span>
                  <span className="text-sm text-fg-dim">{capability.description}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-xs text-fg-dim">
          BlindBook™ — The World&apos;s First Hunting Operating System
        </p>
      </aside>

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-12 lg:min-h-0 lg:px-10">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
          style={{ backgroundImage: "url('/auth-background.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-sidebar/85 lg:hidden" aria-hidden />
        <div
          className="pointer-events-none absolute inset-0 hidden lg:block"
          style={{
            background:
              'radial-gradient(60% 45% at 50% 38%, var(--blaze-soft) 0%, transparent 70%)',
          }}
          aria-hidden
        />
        <main className="relative z-10 w-full max-w-md" data-theme="dark">
          <div className="flex justify-center">
            <div className="relative z-10 flex h-24 w-24 translate-y-1/2 items-center justify-center rounded-full bg-sidebar shadow-[0_-4px_12px_rgba(0,0,0,0.2)] lg:mb-9 lg:translate-y-0">
              <img
                src="/blindbook-logo.svg"
                alt="BlindBook"
                width={56}
                height={56}
                className="brightness-0 invert"
              />
            </div>
          </div>
          <Card className="w-full gap-0 border-border-strong px-8 pb-10 pt-18 lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
            <div className="flex flex-col items-start gap-3">
              <h1 className="text-3xl font-bold tracking-tight lg:text-4xl">{title}</h1>
              {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
              {headerSlot ?? null}
            </div>
            <div className="mt-9">{children}</div>
            {footer ? (
              <div className="mt-7 text-center text-sm text-muted-foreground">{footer}</div>
            ) : null}
          </Card>
        </main>
      </div>
    </div>
  )
}
